import React from "react";
import { useFela } from "react-fela";
import useTranslation from "next-translate/useTranslation";

import { Grid } from "@/components/Grid";
import { Heading } from "@/components/Heading";
import { Button } from "@/components/Button";
import { LinkButton } from "@/components/LinkButton";
import { useTheme } from "@/hooks/useTheme";

import { IColorfulCTASection } from "./types";

type IProps = IColorfulCTASection & JSX.IntrinsicElements["div"];

export const ColorfulCTASection: React.FC<IProps> = ({
  title,
  subtitle,
  buttonLabel,
  href,
  counter,
  onButtonClick,
  ...rest
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();
  const { t } = useTranslation();

  const singleLine = !subtitle && !counter;

  const buttonStyles = {
    width: "auto",
    flexShrink: 0,
    padding: "0 26px",
    boxShadow: "0 18px 54px 0 rgba(255, 111, 2, 0.5)",
    minWidth: singleLine ? 190 : 0,
  };

  return (
    <Grid
      id="ColorfulCTASection"
      wrapperStyle={{
        height: 414,
        backgroundImage: `url('/svgs/bubbles-overlay.svg'), linear-gradient(53deg, ${colors.GRADIENT_BLUE_DARK} 26%, ${colors.GRADIENT_PURPLE} 127%)`,
        backgroundSize: "cover",
      }}
      customStyle={{
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        [bp.FROM_TABLET]: {
          flexDirection: "row",
        },
      }}
      {...rest}
    >
      <div
        className={css({
          marginRight: 0,
          marginBottom: 20,
          [bp.FROM_TABLET]: {
            marginRight: singleLine ? 100 : 165,
            marginBottom: 0,
          },
        })}
      >
        <Heading
          as={singleLine ? "h2" : "h1"}
          customStyle={{
            letterSpacing: 1.33,
            color: colors.WHITE,
            [bp.FROM_TABLET]: {
              textAlign: "left",
              marginBottom: singleLine ? "0 !important" : 24,
            },
          }}
        >
          {title}
        </Heading>
        {subtitle && (
          <p
            className={css({
              fontSize: 18,
              letterSpacing: ".5px",
              color: colors.WHITE,
              fontWeight: "bold",
            })}
          >
            {subtitle}
          </p>
        )}
      </div>
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        {href && (
          <LinkButton
            title={buttonLabel || t("section/colorfulCta:buttonDefaultLabel")}
            href={href[0] !== "#" ? href : undefined}
            scrollToId={href[0] === "#" ? href.slice(1) : undefined}
            customStyle={buttonStyles}
          />
        )}
        {onButtonClick && (
          <Button onClick={onButtonClick} customStyle={buttonStyles}>
            {buttonLabel || t("section/colorfulCta:buttonDefaultLabel")}
          </Button>
        )}
      </div>
    </Grid>
  );
};
